/** Saved for Later - localStorage-based save functionality with IKEA-style UI */
(function () {
const SavedForLaterUtils = window.DutchXXLTheme;

const SavedForLater = {
  KEY: "saved_for_later_items",
  MAX_ITEMS: 24,
  MAX_STORAGE_CHARS: 50000,
  MAX_PROPERTIES: 20,
  MAX_PROPERTY_NAME_CHARS: 255,
  MAX_PROPERTY_VALUE_CHARS: 2048,
  MAX_PROPERTY_TOTAL_CHARS: 12000,
  toastTimeout: null,

  storage() {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  },

  /** Get all saved items, dropping malformed or hostile entries */
  get() {
    const storage = this.storage();
    if (!storage) return [];

    try {
      const rawValue = storage.getItem(this.KEY) || "[]";
      if (rawValue.length > this.MAX_STORAGE_CHARS) {
        this.save([]);
        return [];
      }

      const parsed = JSON.parse(rawValue);
      if (!Array.isArray(parsed)) {
        this.save([]);
        return [];
      }

      const items = [];
      const seen = new Set();

      parsed.slice(-this.MAX_ITEMS).forEach((item) => {
        const normalized = this.normalizeItem(item);
        if (!normalized || seen.has(normalized.key)) return;
        seen.add(normalized.key);
        items.push(normalized);
      });

      if (items.length !== parsed.length || JSON.stringify(items) !== JSON.stringify(parsed)) {
        this.save(items);
      }

      return items;
    } catch {
      return [];
    }
  },

  /** Save sanitized items to localStorage */
  save(items) {
    const storage = this.storage();
    if (!storage) return false;

    try {
      const normalizedItems = Array.isArray(items)
        ? items.map((item) => this.normalizeItem(item)).filter(Boolean)
        : [];
      storage.setItem(this.KEY, JSON.stringify(normalizedItems.slice(-this.MAX_ITEMS)));
      return true;
    } catch {
      return false;
    }
  },

  /** Add item and dedupe by variant/properties/selling-plan identity */
  add(item) {
    const normalized = this.normalizeItem(item);
    if (!normalized) return null;

    const items = this.get();
    const existingIndex = items.findIndex((savedItem) => savedItem.key === normalized.key);
    const previousItem = existingIndex >= 0 ? items[existingIndex] : null;
    const nextItems = [...items];

    if (existingIndex >= 0) {
      nextItems[existingIndex] = normalized;
    } else {
      nextItems.push(normalized);
    }

    if (!this.save(nextItems)) return null;
    return { item: normalized, previousItem };
  },

  /** Roll back a just-added saved item if the matching cart mutation failed */
  rollbackAdd(result) {
    if (!result?.item) return false;

    const items = this.get().filter((savedItem) => savedItem.key !== result.item.key);
    if (result.previousItem) items.push(result.previousItem);
    return this.save(items);
  },

  /** Remove item by saved key or legacy variant ID */
  remove(identifier) {
    const id = String(identifier || "");
    const items = this.get().filter(
      (item) => item.key !== id && item.id !== id && item.variantId !== id,
    );
    return this.save(items);
  },

  /** Render saved items section */
  render() {
    const container = document.querySelector("[data-saved-for-later]");
    const grid = document.querySelector("[data-saved-for-later-grid]");
    if (!container || !grid) return;

    const items = this.get();
    if (!items.length) {
      container.style.display = "none";
      grid.replaceChildren();
      return;
    }

    container.style.display = "";
    grid.replaceChildren(...items.map((item) => this.buildCard(item)));

    grid.onclick = (e) => {
      const addBtn = e.target.closest("[data-add-saved]");
      const removeBtn = e.target.closest("[data-remove-saved]");

      if (addBtn) {
        e.preventDefault();
        const key = addBtn.dataset.addSaved;
        const item = this.findItem(key);
        if (item) {
          addBtn.closest(".saved-item")?.classList.add("saved-item--loading");
          this.addToCart(item);
        }
      }

      if (removeBtn) {
        e.preventDefault();
        const key = removeBtn.dataset.removeSaved;
        const item = this.findItem(key);
        this.remove(key);
        this.render();
        if (item) this.showToast(`${item.title} verwijderd`);
      }
    };
  },

  /** Build card DOM - IKEA style with quantity */
  buildCard(item) {
    const card = this.element("div", "saved-item");
    const title = item.title || "Product";
    card.dataset.savedItem = item.key;

    const removeBtn = this.element("button", "saved-item__btn--remove", "×");
    removeBtn.type = "button";
    removeBtn.dataset.removeSaved = item.key;
    removeBtn.setAttribute("aria-label", `Verwijder ${title}`);
    card.append(removeBtn);

    const mediaLink = this.element("a", "saved-item__image");
    mediaLink.href = item.url;

    if (item.image) {
      const image = this.element("img");
      image.src = item.image;
      image.alt = title;
      image.loading = "lazy";
      image.width = 120;
      image.height = 180;
      mediaLink.append(image);
    } else {
      const placeholder = this.element("span", "saved-item__image-placeholder", "Geen afbeelding");
      mediaLink.append(placeholder);
    }

    card.append(mediaLink);

    const info = this.element("div", "saved-item__info");
    const titleLink = this.element("a", "saved-item__title", title);
    titleLink.href = item.url;
    info.append(titleLink);

    if (item.variant) {
      info.append(this.element("span", "saved-item__variant", item.variant));
    }

    if (item.sellingPlanName) {
      info.append(this.element("span", "saved-item__selling-plan", item.sellingPlanName));
    }

    const visibleProperties = this.visibleProperties(item.properties);
    if (visibleProperties.length) {
      const list = this.element("ul", "saved-item__properties");
      list.setAttribute("aria-label", `Eigenschappen voor ${title}`);
      visibleProperties.forEach(([name, value]) => {
        const entry = this.element("li", "saved-item__property");
        entry.append(
          this.element("span", "saved-item__property-name", `${name}: `),
          document.createTextNode(value),
        );
        list.append(entry);
      });
      info.append(list);
    }

    info.append(
      this.element("span", "saved-item__price", SavedForLaterUtils.formatMoney(item.price)),
      this.element("span", "saved-item__qty", `Aantal: ${item.quantity}`),
    );
    card.append(info);

    const actions = this.element("div", "saved-item__actions");
    const addBtn = this.element("button", "saved-item__btn--add", "Toevoegen");
    addBtn.type = "button";
    addBtn.dataset.addSaved = item.key;
    addBtn.setAttribute("aria-label", `Voeg ${title} toe aan winkelwagen`);
    addBtn.append(this.arrowIcon());
    actions.append(addBtn);
    card.append(actions);

    return card;
  },

  /** Add saved item back to cart */
  async addToCart(item) {
    const savedItem = this.normalizeItem(item);
    if (!savedItem) return;

    const body = {
      id: savedItem.variantId,
      quantity: savedItem.quantity,
    };
    if (Object.keys(savedItem.properties).length) body.properties = savedItem.properties;
    if (savedItem.sellingPlan) body.selling_plan = savedItem.sellingPlan;

    try {
      await SavedForLaterUtils.postCartJson("add.js", body, false);

      this.remove(savedItem.key);
      this.showToast(`${savedItem.title} toegevoegd aan winkelwagen`);
      location.reload();
    } catch {
      this.showToast("Er ging iets fout. Probeer opnieuw.");
      this.findCard(savedItem.key)?.classList.remove("saved-item--loading");
    }
  },

  normalizeItem(item) {
    if (!item || typeof item !== "object" || Array.isArray(item)) return null;

    const variantId = this.normalizeId(item.variantId ?? item.id);
    if (!variantId) return null;

    const properties = this.normalizeProperties(item.properties);
    const sellingPlan = this.normalizeSellingPlan(item.sellingPlan ?? item.selling_plan);
    const normalized = {
      key: "",
      id: variantId,
      variantId,
      lineKey: this.limitText(item.lineKey ?? "", 180),
      title: this.limitText(item.title || "Product", 180) || "Product",
      variant: this.limitText(item.variant || "", 120),
      sellingPlan,
      sellingPlanName: this.limitText(item.sellingPlanName ?? item.selling_plan_name ?? "", 120),
      image: this.safeImageUrl(item.image),
      price: this.normalizeCents(item.price),
      url: this.safeProductUrl(item.url),
      quantity: this.normalizeQuantity(item.quantity),
      properties,
      added: this.normalizeTimestamp(item.added),
    };

    normalized.key = this.identityKey(normalized);
    return normalized;
  },

  normalizeId(value) {
    const id = String(value || "").trim();
    return /^\d+$/.test(id) ? id : "";
  },

  normalizeSellingPlan(value) {
    const id = String(value || "").trim();
    return /^\d+$/.test(id) ? id : "";
  },

  normalizeQuantity(value) {
    const quantity = Number.parseInt(value, 10);
    if (!Number.isFinite(quantity)) return 1;
    return Math.max(1, Math.min(quantity, 99));
  },

  normalizeCents(value) {
    const cents = Number.parseInt(value, 10);
    if (!Number.isFinite(cents)) return 0;
    return Math.max(0, Math.min(cents, 999999999));
  },

  normalizeTimestamp(value) {
    const timestamp = Number(value);
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now();
  },

  normalizeProperties(value) {
    let raw = value;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw || "{}");
      } catch {
        raw = {};
      }
    }

    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};

    const properties = {};
    let totalLength = 0;

    Object.entries(raw).some(([name, propertyValue]) => {
      if (Object.keys(properties).length >= this.MAX_PROPERTIES) return true;
      if (!["string", "number", "boolean"].includes(typeof propertyValue)) return false;

      const key = String(name ?? "");
      const val = String(propertyValue);
      if (!key || !val || this.isUnsafePropertyName(key)) return false;
      if (key.length > this.MAX_PROPERTY_NAME_CHARS || val.length > this.MAX_PROPERTY_VALUE_CHARS) {
        return false;
      }

      totalLength += key.length + val.length;
      if (totalLength > this.MAX_PROPERTY_TOTAL_CHARS) return true;

      // Preserve payload keys/values exactly for Shopify add-back fidelity.
      // Display-only escaping/truncation is handled separately in visibleProperties().
      properties[key] = val;
      return false;
    });

    return properties;
  },

  limitText(value, maxLength = 160) {
    return String(value ?? "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, maxLength);
  },

  isUnsafePropertyName(name) {
    return ["__proto__", "constructor", "prototype"].includes(String(name));
  },

  safeProductUrl(value) {
    const fallback = (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/");
    const raw = String(value || "").trim();
    if (!raw) return fallback;

    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin || !["http:", "https:"].includes(url.protocol)) {
        return fallback;
      }
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return fallback;
    }
  },

  safeImageUrl(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";

    try {
      const url = new URL(raw, window.location.origin);
      const host = url.hostname.toLowerCase();
      const isAllowedHost =
        url.origin === window.location.origin ||
        host === "shopify.com" ||
        host.endsWith(".shopify.com") ||
        host === "myshopify.com" ||
        host.endsWith(".myshopify.com") ||
        host === "shopifycdn.net" ||
        host.endsWith(".shopifycdn.net");

      if (!["http:", "https:"].includes(url.protocol) || !isAllowedHost) return "";
      return url.href;
    } catch {
      return "";
    }
  },

  visibleProperties(properties) {
    return Object.entries(properties || {})
      .filter(([name, value]) => value && !String(name).startsWith("_"))
      .map(([name, value]) => [this.limitText(name, 80), this.limitText(value, 240)])
      .filter(([name, value]) => name && value)
      .slice(0, 4);
  },

  identityKey(item) {
    const source = [
      item.variantId,
      item.sellingPlan || "",
      this.stableProperties(item.properties),
    ].join("|");
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) {
      hash = (hash << 5) - hash + source.charCodeAt(index);
      hash |= 0;
    }
    return `${item.variantId}:${Math.abs(hash).toString(36)}`;
  },

  stableProperties(properties) {
    return JSON.stringify(
      Object.keys(properties || {})
        .sort()
        .reduce((ordered, key) => {
          ordered[key] = properties[key];
          return ordered;
        }, {}),
    );
  },

  findItem(key) {
    return this.get().find((item) => item.key === key || item.id === key || item.variantId === key);
  },

  findCard(key) {
    return [...document.querySelectorAll("[data-saved-item]")].find(
      (card) => card.dataset.savedItem === key,
    );
  },

  element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  },

  arrowIcon() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("width", "18");
    svg.setAttribute("height", "18");
    svg.setAttribute("aria-hidden", "true");

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M5 12h14M12 5l7 7-7 7");
    svg.append(path);
    return svg;
  },

  /** Show toast notification */
  showToast(message) {
    const toast = document.querySelector("[data-saved-toast]");
    const text = document.querySelector("[data-saved-toast-text]");
    if (!toast || !text) return;

    clearTimeout(this.toastTimeout);

    text.textContent = message;
    toast.classList.add("saved-toast--visible");

    this.toastTimeout = setTimeout(() => this.hideToast(), 4000);
  },

  /** Hide toast notification */
  hideToast() {
    const toast = document.querySelector("[data-saved-toast]");
    if (toast) {
      toast.classList.remove("saved-toast--visible");
    }
    clearTimeout(this.toastTimeout);
  },

  /** Initialize */
  init() {
    this.render();

    document
      .querySelector("[data-saved-toast-close]")
      ?.addEventListener("click", () => this.hideToast());
  },
};

window.SavedForLater = SavedForLater;

// Initialize on cart page
if (document.querySelector("[data-saved-for-later]")) {
  SavedForLater.init();
}
})();
